/**
 * @file        src/components/SeoFields.jsx
 * @description "Search & social" form block for a content item that has a
 *              storefront page of its own (a blog post, a package — migration
 *              077). Meta title / description / keywords, a social share image,
 *              a canonical URL override and an index toggle, each empty field
 *              saying what it falls back to, plus a live Google-result preview.
 *
 *              THE PREVIEW IS THE SERVER'S ANSWER, NEVER A CLIENT GUESS. It is
 *              rendered from POST /api/seo/preview, which runs the same resolver
 *              (utils/seoResolve.js) the storefront's HTML uses — title template,
 *              brand defaults, canonical host and all. Composing the title here
 *              would be a second definition of it, and the two would drift.
 *              If the endpoint is unavailable the block says "Preview
 *              unavailable" and the form keeps working.
 *
 *              Blogs and packages are shared by every brand, so the preview has
 *              its own brand picker (the item is not brand-scoped; the RESULT
 *              is, because each storefront has its own template and host).
 *
 *              Must be rendered inside a `.form-grid`; every row is a `.field`.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (per-item SEO on blogs + packages).
 */
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import ImageUpload from './ImageUpload';
import Select from './Select';
import { useBrands } from '../api/brands';
import { post } from '../api/client';

/** Lengths Google typically shows before truncating. Soft limits only. */
const TITLE_SOFT = 60;
const DESC_SOFT = 160;
/** Hard limits — the column widths (migration 077). */
const TITLE_MAX = 255;
const TEXT_MAX = 500;
const PREVIEW_DEBOUNCE_MS = 400;
/** A blog body can be 200k chars of HTML; the resolver only needs its opening. */
const PREVIEW_BODY_CAP = 5000;

export const SEO_EMPTY = {
  meta_title: '', meta_description: '', meta_keyword: '',
  og_image_url: '', canonical_url: '', robots_index: true,
};

/** Form state from a stored row (a row from an un-migrated tenant has no SEO keys). */
export function seoFromRow(row) {
  return {
    meta_title: row?.meta_title || '',
    meta_description: row?.meta_description || '',
    meta_keyword: row?.meta_keyword || '',
    og_image_url: row?.og_image_url || '',
    canonical_url: row?.canonical_url || '',
    robots_index: row?.robots_index == null ? true : Boolean(Number(row.robots_index)),
  };
}

/**
 * Request body fragment. Always sends every key — a PATCH that omits a field
 * leaves the stored value untouched, so clearing one must send '' explicitly.
 */
export function seoBody(form) {
  return {
    meta_title: (form.meta_title || '').trim(),
    meta_description: (form.meta_description || '').trim(),
    meta_keyword: (form.meta_keyword || '').trim(),
    og_image_url: (form.og_image_url || '').trim(),
    canonical_url: (form.canonical_url || '').trim(),
    robots_index: Boolean(form.robots_index),
  };
}

/**
 * The slug the server will derive on save (blogs/packages services' slugify),
 * so a NEW item's preview can show its future address. Only the URL path —
 * the preview's title, description and host are always the server's.
 */
export function previewSlug(input) {
  return String(input || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function isAbsoluteHttp(v) {
  try { const u = new URL(v); return u.protocol === 'http:' || u.protocol === 'https:'; } catch { return false; }
}

/** Client-side mirror of the route schema. The server is the boundary. */
export function seoErrors(form) {
  const e = {};
  const b = seoBody(form);
  if (b.meta_title.length > TITLE_MAX) e.meta_title = `At most ${TITLE_MAX} characters`;
  if (b.meta_description.length > TEXT_MAX) e.meta_description = `At most ${TEXT_MAX} characters`;
  if (b.meta_keyword.length > TEXT_MAX) e.meta_keyword = `At most ${TEXT_MAX} characters`;
  if (b.og_image_url && !isAbsoluteHttp(b.og_image_url)) e.og_image_url = 'Must be a full http(s) URL';
  if (b.canonical_url && !isAbsoluteHttp(b.canonical_url)) e.canonical_url = 'Must be a full http(s) URL, e.g. https://www.example.com/blog/my-post';
  return e;
}

function useDebounced(value, ms) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

const plainText = (html) => String(html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
const clip = (s, n = 70) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** What each empty field falls back to, per item kind. */
function fallbacks(itemKind, item) {
  const isBlog = itemKind === 'blog';
  const name = (isBlog ? item.title : item.name) || '';
  const excerpt = isBlog ? (item.heading || plainText(item.description)) : plainText(item.description);
  const image = isBlog ? item.image : (item.hero_image || (Array.isArray(item.images) ? item.images[0] : ''));
  const noun = isBlog ? 'post' : 'package';
  return {
    title: name
      ? <>Empty uses the {noun} {isBlog ? 'title' : 'name'}: <b>{clip(name)}</b></>
      : <>Empty uses the {noun} {isBlog ? 'title' : 'name'}.</>,
    description: excerpt
      ? <>Empty uses the {isBlog ? 'post heading (or the start of the body)' : 'package description'}: <b>{clip(excerpt, 90)}</b>, then the page and brand defaults under SEO Management → Page SEO.</>
      : <>Empty uses the {isBlog ? 'post heading (or the start of the body)' : 'package description'}, then the page and brand defaults under SEO Management → Page SEO.</>,
    keywords: <>Empty uses the page default under SEO Management → Page SEO. Google ignores this tag; a few other search engines still read it.</>,
    image: image
      ? <>Empty uses the {isBlog ? 'post image' : 'package hero image'}, then the brand&apos;s default share image.</>
      : <>Empty uses the brand&apos;s default share image (this {noun} has no image of its own).</>,
    canonical: <>Leave empty to use this {noun}&apos;s own address on the brand&apos;s canonical host. Only set it when this content is a copy of a page that lives elsewhere — search engines then credit that URL instead.</>,
  };
}

function Counter({ len, soft }) {
  const over = len > soft;
  return (
    <span style={{ float: 'right', fontWeight: 600, color: over ? 'var(--amber)' : 'var(--muted)' }}>
      {len}/{soft}
    </span>
  );
}

export default function SeoFields({ value, onChange, errors = {}, itemKind, item = {} }) {
  const set = (k) => (v) => onChange({ ...value, [k]: v });
  const fb = fallbacks(itemKind, item);
  const titleLen = (value.meta_title || '').trim().length;
  const descLen = (value.meta_description || '').trim().length;

  return (
    <>
      <div className="field full"><label className="section-label">Search &amp; social</label></div>

      <Field label={<>Meta title <Counter len={titleLen} soft={TITLE_SOFT} /></>} err={errors.meta_title} full
             hint={titleLen > TITLE_SOFT
               ? <>Longer than {TITLE_SOFT} characters — Google will likely cut it off. {fb.title}</>
               : fb.title}>
        <input value={value.meta_title} maxLength={TITLE_MAX}
               onChange={(e) => set('meta_title')(e.target.value)} placeholder="Title shown in search results" />
      </Field>

      <Field label={<>Meta description <Counter len={descLen} soft={DESC_SOFT} /></>} err={errors.meta_description} full
             hint={descLen > DESC_SOFT
               ? <>Longer than {DESC_SOFT} characters — Google will likely cut it off. {fb.description}</>
               : fb.description}>
        <textarea rows={3} value={value.meta_description} maxLength={TEXT_MAX}
                  onChange={(e) => set('meta_description')(e.target.value)}
                  placeholder="One or two sentences shown under the title in search results" />
      </Field>

      <Field label="Meta keywords" err={errors.meta_keyword} full hint={fb.keywords}>
        <input value={value.meta_keyword} maxLength={TEXT_MAX}
               onChange={(e) => set('meta_keyword')(e.target.value)} placeholder="comma, separated, keywords" />
      </Field>

      <Field label="Social share image" err={errors.og_image_url} full hint={fb.image}>
        <ImageUpload value={value.og_image_url} onChange={set('og_image_url')} />
      </Field>

      <Field label="Canonical URL override" err={errors.canonical_url} full hint={fb.canonical}>
        <input value={value.canonical_url} maxLength={TEXT_MAX}
               onChange={(e) => set('canonical_url')(e.target.value)} placeholder="https://…  (absolute URL)" />
      </Field>

      <Field label="Search engines" full
             hint={value.robots_index
               ? 'Indexable, unless the page or brand settings say otherwise (those can only remove visibility).'
               : 'This page will be served with noindex — it will drop out of search results.'}>
        <label className="check">
          <input type="checkbox" checked={Boolean(value.robots_index)}
                 onChange={(e) => set('robots_index')(e.target.checked)} />
          <span>Allow search engines to index this page</span>
        </label>
      </Field>

      <SeoPreview value={value} itemKind={itemKind} item={item} />
    </>
  );
}

/** Live Google-result preview, resolved by the server for the chosen brand. */
function SeoPreview({ value, itemKind, item }) {
  const { options, isLoading: brandsLoading } = useBrands();
  const [brand, setBrand] = useState('');
  useEffect(() => {
    // Default to the first registered brand; the item itself belongs to all.
    if (!brand && options.length) setBrand(options[0].value);
  }, [brand, options]);

  const draft = useMemo(() => ({
    ...item,
    description: typeof item.description === 'string' ? item.description.slice(0, PREVIEW_BODY_CAP) : item.description,
    ...seoBody(value),
  }), [item, value]);
  const debounced = useDebounced(draft, PREVIEW_DEBOUNCE_MS);

  const q = useQuery({
    queryKey: ['seo', 'preview', itemKind, brand, debounced],
    queryFn: () => post('/seo/preview', { brand, itemKind, item: debounced }, { silent: true }).then((r) => r.data),
    enabled: Boolean(brand),
    placeholderData: keepPreviousData,
    retry: false,
    staleTime: 30 * 1000,
  });

  const r = q.data;
  const noindex = r && typeof r.robots === 'string' && /noindex/i.test(r.robots);
  const ogImage = r?.og?.image || null;

  return (
    <div className="field full">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <label>Search result preview</label>
        <div style={{ minWidth: 180 }}>
          <Select value={brand} onChange={setBrand} options={options}
                  placeholder={brandsLoading ? 'Loading…' : 'Select brand'} />
        </div>
      </div>
      <div style={{
        border: '1px solid var(--stroke)', borderRadius: 12, padding: '14px 16px',
        background: 'var(--field)', minHeight: 104, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        {q.isError ? (
          <div className="muted" style={{ fontSize: '.8rem' }}>
            Preview unavailable. Your changes still save normally.
          </div>
        ) : !brand ? (
          <div className="muted" style={{ fontSize: '.8rem' }}>
            {brandsLoading ? 'Loading brands…' : 'No brand is registered, so there is nothing to preview against.'}
          </div>
        ) : !r ? (
          <div className="muted" style={{ fontSize: '.8rem' }}>Building preview…</div>
        ) : (
          <>
            <div style={{ flex: 1, minWidth: 0, fontFamily: 'arial, sans-serif', opacity: q.isFetching ? 0.6 : 1, transition: 'opacity .2s' }}>
              <div style={{ color: '#006621', fontSize: 13, overflowWrap: 'anywhere' }}>{r.canonical || '—'}</div>
              <div style={{ color: '#1a0dab', fontSize: 18, lineHeight: 1.3, margin: '2px 0', overflowWrap: 'anywhere' }}>{r.title || '—'}</div>
              <div style={{ color: '#545454', fontSize: 13, lineHeight: 1.45, overflowWrap: 'anywhere' }}>{r.description || '—'}</div>
              {noindex ? (
                <div className="field-hint" style={{ color: 'var(--amber)' }}>
                  Served as <b>{r.robots}</b> — this page will not appear in search results.
                </div>
              ) : null}
            </div>
            {ogImage ? (
              <img src={ogImage} alt="Social share" style={{ width: 96, height: 60, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, err, hint, full, children }) {
  return (
    <div className={`field${full ? ' full' : ''}`}>
      <label>{label}</label>
      {children}
      {hint && !err ? <div className="field-hint">{hint}</div> : null}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
